// Shared client-side estimate math for the "price this car" configurator.
//
// ONE implementation, two callers: the /prices/:make/:model/:year island
// (price_configurator_controller, payload rendered inline by the server) and
// /value-my-car (value_my_car_controller, same payload fetched as JSON from
// SeoPagesController#value_estimate). The payload shape is produced in Ruby
// by SeoPagesHelper#seo_configurator_payload — keep the two in step.

export function findTrim(cfg, slug) {
  if (!cfg || !cfg.trims) return null;
  return cfg.trims.find((t) => t.slug === slug) || null;
}

export function findState(cfg, code) {
  if (!cfg || !cfg.states) return null;
  return cfg.states.find((s) => s.code === code) || null;
}

// Estimate = trim median (or national) -> mileage adjustment along the
// $/10K-mile slope -> state multiplier (state typical / national median,
// clamped to ±15% so one thin state row can't produce nonsense).
export function estimateFor(cfg, { trim = null, miles = null, state = null } = {}) {
  const base = trim ? trim.median : cfg.nationalMedian;
  let value = base;
  const at = miles === null ? cfg.avgMileage : miles;
  if (cfg.slopePer10k) {
    value += cfg.slopePer10k * ((cfg.avgMileage - at) / 10000);
  }
  if (state) {
    const mult = Math.min(1.15, Math.max(0.85, state.typical / cfg.nationalMedian));
    value *= mult;
  }
  return Math.max(Math.round(value / 50) * 50, Math.round(base * 0.4));
}

// Fair range: reuse the selected trim's own IQR shape when we have it, else
// the page-level IQR shape, else a flat ±6%.
export function fairRange(cfg, estimate, trim = null) {
  let loRatio = 0.94;
  let hiRatio = 1.06;
  const src = (trim && trim.p25 && trim.p75 && trim.median > 0) ? trim : null;
  if (src) {
    loRatio = src.p25 / src.median;
    hiRatio = src.p75 / src.median;
  } else if (cfg.p25 && cfg.p75 && cfg.nationalMedian > 0) {
    loRatio = cfg.p25 / cfg.nationalMedian;
    hiRatio = cfg.p75 / cfg.nationalMedian;
  }
  return {
    low: Math.round((estimate * loRatio) / 50) * 50,
    high: Math.round((estimate * hiRatio) / 50) * 50,
  };
}

export const usd = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});
